// src/components/SystemRecommendation/components/FitsYourFuture.tsx

import React from 'react';
import { FutureFitData, ViewMode } from '../types';
import CollapsibleSection from './CollapsibleSection';

interface FitsYourFutureProps {
  data: FutureFitData;
  viewMode: ViewMode;
}

const FitsYourFuture: React.FC<FitsYourFutureProps> = ({ data, viewMode }) => {
  return (
    <CollapsibleSection
      title="This is how it fits your future"
      icon="🔮"
      confidenceLevel={data.confidence.level}
      summary={data.confidence.summary}
    >
      {viewMode === 'customer' ? (
        <div className="customer-content">
          <div className="fit-section">
            <h4>Lifespan & Warranty</h4>
            <p><strong>Expected lifespan:</strong> {data.lifespan.expected}</p>
            <p><strong>Warranty coverage:</strong> {data.lifespan.warranty}</p>
          </div>

          {data.smartHome.compatible && (
            <div className="fit-section">
              <h4>Smart Home Integration</h4>
              <p className="success-text">✓ Smart home compatible</p>
              <ul>
                {data.smartHome.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="fit-section">
            <h4>Future Upgrades</h4>
            {data.upgradePath.futureProof && (
              <p className="success-text">✓ Future-proof design</p>
            )}
            {data.upgradePath.options.length > 0 && (
              <ul>
                {data.upgradePath.options.map((option, i) => (
                  <li key={i}>{option}</li>
                ))}
              </ul>
            )}
          </div>

          <div className="fit-section">
            <h4>Environmental Impact</h4>
            <p><strong>Carbon reduction:</strong> {data.environmental.carbonReduction}</p>
            <p><strong>Efficiency:</strong> {data.environmental.efficiency}</p>
          </div>

          <div className="fit-section">
            <h4>Future Regulations</h4>
            <p>
              {data.regulations.compliant ? (
                <span className="success-text">✓ Meets current regulations</span>
              ) : (
                <span className="warning-text">⚠️ Verify compliance</span>
              )}
            </p>
            <p className="detail-text">{data.regulations.futureProof}</p>
          </div>
        </div>
      ) : (
        <div className="technical-content">
          <div className="tech-grid">
            <div className="tech-item">
              <span className="tech-label">Expected Lifespan:</span>
              <span className="tech-value">{data.lifespan.expected}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Warranty:</span>
              <span className="tech-value">{data.lifespan.warranty}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Smart Compatible:</span>
              <span className="tech-value">{data.smartHome.compatible ? 'Yes' : 'No'}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Future-proof:</span>
              <span className="tech-value">{data.upgradePath.futureProof ? 'Yes' : 'Limited'}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Carbon Reduction:</span>
              <span className="tech-value">{data.environmental.carbonReduction}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Efficiency:</span>
              <span className="tech-value">{data.environmental.efficiency}</span>
            </div>
            <div className="tech-item">
              <span className="tech-label">Regulations:</span>
              <span className="tech-value">{data.regulations.compliant ? 'Compliant' : 'Review required'}</span>
            </div>
          </div>
          {data.smartHome.compatible && data.smartHome.features.length > 0 && (
            <div className="tech-notes">
              <p><strong>Smart features:</strong></p>
              <ul>
                {data.smartHome.features.map((feature, i) => (
                  <li key={i}>{feature}</li>
                ))}
              </ul>
            </div>
          )}
          {data.upgradePath.options.length > 0 && (
            <div className="tech-notes">
              <p><strong>Upgrade options:</strong></p>
              <ul>
                {data.upgradePath.options.map((option, i) => (
                  <li key={i}>{option}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </CollapsibleSection>
  );
};

export default FitsYourFuture;
